'use client'

import {
  useEffect,
  useEffectEvent,
  useState,
  type ComponentProps,
  type Key,
  type ReactNode,
} from 'react'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from './collapsible'
import { ChevronRightIcon } from './icon'
import { SidebarMenu, SidebarMenuButton, SidebarMenuSub } from './sidebar'
import { cn } from '../lib/utils'

export type SidebarTreeProps = ComponentProps<'nav'> & {
  menuClassName?: string
}

export function SidebarTree({
  children,
  className,
  menuClassName,
  ...props
}: SidebarTreeProps) {
  return (
    <nav className={cn('min-h-0 flex-1 overflow-y-auto', className)} {...props}>
      <SidebarMenu className={cn('gap-0', menuClassName)}>
        {children}
      </SidebarMenu>
    </nav>
  )
}

export type SidebarTreeLeafProps = ComponentProps<typeof SidebarMenuButton>

export function SidebarTreeLeaf({
  className,
  size = 'sm',
  ...props
}: SidebarTreeLeafProps) {
  return (
    <SidebarMenuButton
      className={cn(
        'data-[active=true]:bg-secondary/10 hover:bg-primary/8 active:bg-primary/8 data-[active=true]:hover:bg-secondary/10 data-[active=true]:active:bg-secondary/10 h-auto',
        className,
      )}
      size={size}
      {...props}
    />
  )
}

export interface SidebarTreeBranchProps {
  children: ReactNode
  className?: string
  defaultOpen?: boolean
  forceOpen?: boolean
  onOpenChange?: (open: boolean) => void
  /** Changing this key reopens the branch. Undefined leaves disclosure state alone. */
  revealKey?: Key
  /** Hide the default trailing chevron when the trigger supplies its own indicator. */
  showChevron?: boolean
  trigger: ReactNode
  triggerProps?: Omit<SidebarTreeLeafProps, 'children' | 'isActive'> & {
    [attribute: `data-${string}`]: string | number | boolean | undefined
  }
}

export function SidebarTreeBranch({
  children,
  className,
  defaultOpen = false,
  forceOpen = false,
  onOpenChange,
  revealKey,
  showChevron = true,
  trigger,
  triggerProps,
}: SidebarTreeBranchProps) {
  const [open, setOpen] = useState(defaultOpen || revealKey !== undefined)
  const notifyOpenChangeFromEffect = useEffectEvent((nextOpen: boolean) => {
    onOpenChange?.(nextOpen)
  })

  useEffect(() => {
    if (revealKey === undefined) return
    // eslint-disable-next-line rc/set-state-in-effect -- A new selection reveals its ancestor once; manual collapse still works afterward.
    setOpen(true)
    notifyOpenChangeFromEffect(true)
  }, [revealKey])

  const visible = forceOpen || open

  return (
    <Collapsible
      className={cn('group/sidebar-tree-branch', className)}
      open={visible}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        onOpenChange?.(nextOpen)
      }}
    >
      <CollapsibleTrigger
        render={
          <SidebarMenuButton
            {...triggerProps}
            className={cn('h-auto', triggerProps?.className)}
            size={triggerProps?.size ?? 'sm'}
          >
            {trigger}
            {showChevron && <ChevronRightIcon
              className={cn(
                'ml-auto transition-transform duration-200 ease-out',
                visible && 'rotate-90',
              )}
            />}
          </SidebarMenuButton>
        }
      />
      <CollapsibleContent>
        <SidebarMenuSub className="mr-0 gap-0 pr-0">{children}</SidebarMenuSub>
      </CollapsibleContent>
    </Collapsible>
  )
}
